import { BundleDocumentType } from '../components/BundledDocuments/BundledDocuments.types';

// Helpers around the `bundle-documents/document-categories/static` response.
// The endpoint returns every *static* bundle document grouped by document category,
// which is exactly the set of documents that can be replaced from the upload screen.

export interface StaticCategoryDocument {
    id?: number | string;
    productId: number | string;
    process: number;
    documentType?: number;
    modelPortfolioId?: number | null;
    securityId?: number | null;
    name?: string;
}

export interface StaticDocumentCategory {
    categoryKey: string;
    categoryName: string;
    documents?: StaticCategoryDocument[];
    securityId?: number | null;
    modelPortfolioId?: number | null;
}

// A category is one dropdown entry, except for BIBs: those carry a model portfolio and a
// security, and are listed one entry per pair (e.g. `3a - BIB - Target Investment Fonds 35-B`).
export interface StaticDocumentCategoryGroup {
    value: string;
    categoryKey: string;
    categoryName: string;
    securityId: number | null;
    modelPortfolioId: number | null;
    documents: StaticCategoryDocument[];
}

export interface SecurityOption {
    id: number;
    name?: string;
    isin?: string;
}

export const stripLocaleSuffix = (value = ''): string => value.replace(/\s*\[[a-z]{2}\]$/i, '').trim();

// Only static documents can be replaced from the upload screen. The endpoint is already
// scoped to static documents, so anything without an explicit type is kept.
export const isStaticDocument = (document?: StaticCategoryDocument): boolean => document?.documentType === undefined
    || document?.documentType === null
    || Number(document.documentType) === BundleDocumentType.Static;

export const getBundleDocumentId = (document?: StaticCategoryDocument) => (document as any)?.id
    ?? (document as any)?.bundleDocumentId
    ?? (document as any)?.documentId;

export const matchesDocumentFilters = (
    document: StaticCategoryDocument,
    productIds: string[] = [],
    processIds: number[] = [],
): boolean => (productIds.length === 0 || productIds.includes(String(document?.productId)))
    && (processIds.length === 0 || processIds.includes(Number(document?.process)));

export const buildDocumentCategoryGroups = (
    categories: StaticDocumentCategory[] = [],
): StaticDocumentCategoryGroup[] => {
    const groups = new Map<string, StaticDocumentCategoryGroup>();

    categories.forEach((category) => {
        (category?.documents ?? []).filter(isStaticDocument).forEach((document) => {
            const value = `${category?.categoryKey}::${category?.securityId ?? ''}::${category?.modelPortfolioId ?? ''}`;

            if (!groups.has(value)) {
                groups.set(value, {
                    value,
                    categoryKey: category?.categoryKey,
                    categoryName: stripLocaleSuffix(category?.categoryName),
                    securityId: category?.securityId ?? null,
                    modelPortfolioId: category?.modelPortfolioId ?? null,
                    documents: [],
                });
            }

            groups.get(value)?.documents.push(document);
        });
    });

    return [...groups.values()];
};

export const filterDocumentCategoryGroups = ({
    groups = [],
    productIds = [],
    processIds = [],
}: {
    groups?: StaticDocumentCategoryGroup[];
    productIds?: string[];
    processIds?: number[];
}): StaticDocumentCategoryGroup[] => groups.filter(
    (group) => group.documents.some((document) => matchesDocumentFilters(document, productIds, processIds)),
);

export const getDocumentCategoryOptions = ({
    groups = [],
    securities = [],
}: {
    groups?: StaticDocumentCategoryGroup[];
    securities?: SecurityOption[];
}) => groups.map((group) => {
    const security = securities.find((item) => Number(item?.id) === Number(group.securityId));

    return {
        value: group.value,
        label: security?.name
            ? `${group.categoryName} - ${security.name}`
            : group.categoryName,
    };
});

export const getSecurityIdsFromCategories = (categories: StaticDocumentCategory[] = []): number[] => [
    ...new Set(
        categories
            .map((category) => category?.securityId)
            .filter(Boolean) as number[],
    ),
];
